/* eslint-disable react/jsx-props-no-spreading */
/**
 * Props spreading is required here because this component extends React.ButtonHTMLAttributes
 * and needs to forward all standard button attributes (like aria-* attributes, data-* attributes,
 * and event handlers) to the underlying button element. This is essential for accessibility
 * and proper event handling in the tree structure.
 */
import React from 'react';
import clsx from 'clsx';
import { Dropdown } from 'react-bootstrap';
import { LoadingSpinner } from '../common';
import { useFileExplorerFolder } from '../../hooks/useFileExplorerNode';
import { TreeFolder } from './services';
import { PathNode } from './Context/FileExplorerContext';
import { useFileExplorerContext } from '../../hooks/useFileExplorerContext';
import { DropdownCustomToggle } from '../common/Dropdown';
import {
  EllipsisIcon, FolderIcon,
  RenameIcon,
  TrashIcon,
} from './FileExplorerIcons';
import { useControlledModal } from '../../hooks/useControlledModal';
import { FileExplorerVisualizerActions } from '../../types/fileExplorerTypes';
import { EditFolderModal } from './Modals/EditFolderModal';
import { DeleteFolderModal } from './Modals/DeleteFolderModal';

// Component interfaces
interface FolderToggleButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isActive: boolean;
  isExpanded: boolean;
  hideArrow: boolean;
  userIsSuperuser: boolean;
  isFetched: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

interface FolderChildrenProps {
  folders?: TreeFolder[];
  shouldExpand: boolean;
  collapseId: string;
  parentPath?: PathNode[];
}

// This interface defines the props for the FileExplorerTreeNode component.
// Extends React.ButtonHTMLAttributes<HTMLButtonElement> to allow all standard button attributes.
interface FileExplorerTreeNodeProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  data: TreeFolder;
  parentPath?: PathNode[];
}

// Reusable Components
function FolderToggleButton({
  isActive,
  isExpanded,
  hideArrow,
  userIsSuperuser,
  isFetched,
  onToggle,
  children,
  ...buttonProps
}: FolderToggleButtonProps) {
  return (
    <button
      className="file-explorer-list-navigation-button__content"
      onClick={onToggle}
      type="button"
      {...buttonProps}
    >
      <span className="file-explorer-list-navigation-button__content__icons">
        <span className={clsx('file-explorer-list-navigation-button__content__icons-arrow', {
          expanded: isExpanded,
          hidden: hideArrow || !userIsSuperuser,
        })}
        />
        <span className="file-explorer-list-navigation-button__content__icons-folder" />
      </span>

      <span className="file-explorer-list-navigation-button__content-label">
        {children}
      </span>

      {!isFetched && (
        <span className="spinner">
          <LoadingSpinner size={13} color="info" borderWidth={2} />
        </span>
      )}
    </button>
  );
}

function FolderActionsDropdown({ folder }: { folder: TreeFolder }) {
  const { currentAction, setCurrentAction } = useControlledModal<FileExplorerVisualizerActions>();
  return (
    <>
      <Dropdown className="">
        <Dropdown.Toggle
          as={DropdownCustomToggle}
          className="file-explorer-dropdown"
          id="file-explorer-folder-actions-dropdown-toggle"
        >
          <EllipsisIcon className="rotate-90" />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item
            eventKey="folder.share"
            onClick={() => {
              window.open(folder.shareable_link, '_blank');
            }}
          >
            <FolderIcon />
            Ver carpeta
          </Dropdown.Item>
          <Dropdown.Item
            eventKey="folder.edit"
            onClick={() => setCurrentAction('folder.edit')}
          >
            <RenameIcon />
            Editar
          </Dropdown.Item>
          <Dropdown.Item
            eventKey="folder.delete"
            onClick={() => setCurrentAction('folder.delete')}
          >
            <TrashIcon />
            Eliminar
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      <EditFolderModal
        show={currentAction === 'folder.edit'}
        onHide={() => setCurrentAction(null)}
        data={folder}
      />
      <DeleteFolderModal
        show={currentAction === 'folder.delete'}
        onHide={() => setCurrentAction(null)}
        data={folder}
      />
    </>
  );
}

function FolderChildren({
  folders,
  shouldExpand,
  collapseId,
  parentPath,
}: FolderChildrenProps) {
  return (
    <div
      className={clsx('collapse', {
        'show file-explorer-content': shouldExpand,
      })}
      id={collapseId}
    >
      {folders?.map((folder: TreeFolder) => (
        <li key={folder.id}>
          <FileExplorerTreeNode
            data={folder}
            parentPath={parentPath}
          />
        </li>
      ))}
    </div>
  );
}

export function FileExplorerTreeNode(props: FileExplorerTreeNodeProps) {
  const {
    data,
    parentPath,
  } = props;

  const {

    id, name, permissions, description, remove_file_prefix, shareable_link,
  } = data;

  // Use the custom hook to manage folder state and interactions
  // This hook handles the expansion, toggling, and fetching of folder data
  const {
    isExpanded,
    handleToggle,
    collapseId,
    folders,
    shouldExpand,
    attributes,
    isFetched,
  } = useFileExplorerFolder({
    id, name, parentPath, permissions, description, remove_file_prefix, shareable_link,
  });
  const { userIsSuperuser } = useFileExplorerContext();

  const hideArrow = isFetched && !folders?.length;
  const isActive = attributes['data-active'];

  return (
    <li className="file-explorer-list__item">
      <div
        className={clsx(
          'file-explorer-list-navigation-button btn-group align-items-center w-100',
          {
            'file-explorer-list-navigation-button--active': isActive,
          },
        )}
      >
        <FolderToggleButton
          isActive={isActive}
          isExpanded={isExpanded}
          hideArrow={hideArrow}
          userIsSuperuser={userIsSuperuser}
          isFetched={isFetched}
          onToggle={handleToggle}
          {...attributes}
        >
          {name}
        </FolderToggleButton>
        <FolderActionsDropdown folder={data} />
      </div>

      <FolderChildren
        folders={folders}
        shouldExpand={shouldExpand}
        collapseId={collapseId}
        parentPath={[...(parentPath ?? []), { id, name }]}
      />
    </li>
  );
}
